import { FormTableCellDirtyProvider } from "@/contexts/FormTableCellDirtyContext";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadonlyObject } from "@/types/readOnly";
import { memo, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "../../data-table";
import { useDataTableQuery } from "../../data-table/hooks/useDataTableQuery";
import { useDataTableSettings } from "../../data-table/hooks/useDataTableSettings";
import { PageCard } from "../../PageCard";
import Spinner from "../../Spinner";
import { CardContent } from "../../ui/card";

interface ListContentProps extends PropsWithChildren {
  onResize?: (size: DOMRectReadOnly) => void;
  onScroll?: (left: number, top: number) => void;
  className?: string;
  footer?: React.ReactNode;
  isInDialog?: boolean;
}

export const ListContent = memo(
  <T extends ReadonlyObject, Q extends SuccessFetchListResponse<T>>({
    children,
    onResize,
    onScroll,
    className,
    footer,
    isInDialog,
  }: ListContentProps) => {
    const { t } = useTranslation();
    const { features } = useDataTableSettings<T>();
    const { query } = useDataTableQuery<Q>();

    const innerContent = (
      <>
        <div className="flex flex-col gap-2 border min-h-32 h-full">
          {query.isLoading ? (
            <Spinner
              className="h-full w-full grow"
              label={t("table.loading")}
            />
          ) : (
            <DataTable
              getRowProps={features.getRowProps}
              onResize={onResize}
              onScroll={onScroll}
              footer={footer}
              isInDialog={isInDialog}
            />
          )}
        </div>
        {children}
      </>
    );

    return (
      <PageCard className={className}>
        <CardContent className="flex flex-col gap-2 p-2 justify-between grow h-full">
          {features.editionHighlight ? (
            <FormTableCellDirtyProvider
              baseline={features.editionHighlight.baseline}
              recordKey={features.editionHighlight.recordKey}
            >
              {innerContent}
            </FormTableCellDirtyProvider>
          ) : (
            innerContent
          )}
        </CardContent>
      </PageCard>
    );
  },
);
